import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import {
    KSK1080_R01ItemDto,
    KSK1080_R01WarehouseDto,
    KSK1080_R01LogicCenterDto,
    KSK1080_R01TotalDto,
    KSK1080_R01LotDto,
    ItemSubtotalData,
    WarehouseSubtotalData,
    LogicCenterSubtotalData,
    GridRowData,
} from './Dto_KSK1080_R01';

// 탭 타입 정의 및 UI 상태 타입
export type TabType = 'item' | 'warehouse' | 'logiccenter' | 'total' | 'lot';

export interface TabConfig {
    id: TabType;
    label: string;
    description: string;
}

export type ItemStockData = KSK1080_R01ItemDto;
export type WarehouseStockData = KSK1080_R01WarehouseDto;
export type LogicCenterStockData = KSK1080_R01LogicCenterDto;
export type TotalStockData = KSK1080_R01TotalDto;
export type LotStockData = KSK1080_R01LotDto;

export interface TabData {
    item: ItemStockData[];
    warehouse: WarehouseStockData[];
    logiccenter: LogicCenterStockData[];
    total: TotalStockData[];
    lot: LotStockData[];
}

// 소계 데이터 인터페이스
export interface TabSubtotalData {
    item: ItemSubtotalData[];
    warehouse: WarehouseSubtotalData[];
    logiccenter: LogicCenterSubtotalData[];
    total: any[]; // 전체 탭은 소계 없음
    lot: any[]; // LOT 탭은 소계 없음
}

// 그리드 데이터 인터페이스 (소계 포함)
export interface TabGridData {
    item: GridRowData[];
    warehouse: GridRowData[];
    logiccenter: GridRowData[];
    total: GridRowData[];
    lot: GridRowData[];
}

export interface TabColumnStates {
    item: ColumnState[];
    warehouse: ColumnState[];
    logiccenter: ColumnState[];
    total: ColumnState[];
    lot: ColumnState[];
}

export interface TabLoadingStates {
    item: boolean;
    warehouse: boolean;
    logiccenter: boolean;
    total: boolean;
    lot: boolean;
}

// 탭별 선택된 단일 행 ID (단일 선택)
export interface TabSelectedIds {
    item: string | undefined;
    warehouse: string | undefined;
    logiccenter: string | undefined;
    total: string | undefined;
    lot: string | undefined;
}

// 필터 인터페이스 (API에서 사용)
export interface KSK1080_R01Filter {
    logicCenter: string;
    warehouseCd: string;
    itemCd: string;
	itemNm: string;
    itemGroup: string;
    brandCd: string;
}

interface KSK1080_R01Store {
    // 기본 필터
    KSK1080_R01Filter: KSK1080_R01Filter;
    setKSK1080_R01Filter: (filter: Partial<KSK1080_R01Filter>) => void;
    
    
    // 활성 탭
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    
    // 탭별 데이터
    tabData: TabData;
    setTabData: (tabType: TabType, data: any[]) => void;
    
    // 탭별 컬럼 상태
    tabColumnStates: TabColumnStates;
    setTabColumnState: (tabType: TabType, columnState: any[]) => void;
    
    // 탭별 로딩 상태
    tabLoadingStates: TabLoadingStates;
    setTabLoadingState: (tabType: TabType, loading: boolean) => void;
    
    // 탭별 선택 상태 (단일 선택)
    tabSelectedIds: TabSelectedIds;
    setTabSelectedId: (tabType: TabType, id: string | undefined) => void;
    clearTabSelectedIds: (tabType: TabType) => void;
    
    // === 소계 관련 상태 ===
    showSubtotals: boolean;
    setShowSubtotals: (show: boolean) => void;
    
    // 탭별 소계 데이터
    tabSubtotalData: TabSubtotalData;
    setTabSubtotalData: (tabType: TabType, data: any[]) => void;
    
    // 탭별 그리드 데이터 (소계 포함)
    tabGridData: TabGridData;
    setTabGridData: (tabType: TabType, data: GridRowData[]) => void;
    
    // 초기화
    initData: () => void;
}

export const defaultKSK1080_R01Filter: KSK1080_R01Filter = {
    logicCenter: '',
    warehouseCd: '',
    itemCd: '',
    itemNm: '',
    itemGroup: '',
    brandCd: ''
}

export const useKSK1080_R01Store = create<KSK1080_R01Store>((set, get) => ({
    // 기본 필터
    KSK1080_R01Filter: {
        logicCenter: '',
        warehouseCd: '',
        itemCd: '',
		itemNm: '',
        itemGroup: '',
        brandCd: ''
    },
    setKSK1080_R01Filter: (filter) => set((state) => ({
        KSK1080_R01Filter: { ...state.KSK1080_R01Filter, ...filter }
    })),
    
    // 활성 탭
    activeTab: 'total',
    setActiveTab: (tab) => {
        set({ activeTab: tab });
    },
    
    // 탭별 데이터
    tabData: {
        total: [],
        item: [],
        warehouse: [],
        logiccenter: [],
        lot: []
    },
    setTabData: (tabType, data) => set((state) => ({
        tabData: {
            ...state.tabData,
            [tabType]: data
        }
    })),
    
    // 탭별 컬럼 상태
    tabColumnStates: {
        total: [],
        item: [],
        warehouse: [],
        logiccenter: [],
        lot: []
    },
    setTabColumnState: (tabType, columnState) => set((state) => ({
        tabColumnStates: {
            ...state.tabColumnStates,
            [tabType]: columnState
        }
    })),
    
    // 탭별 로딩 상태
    tabLoadingStates: {
        total: false,
        item: false,
        warehouse: false,
        logiccenter: false,
        lot: false
    },
    setTabLoadingState: (tabType, loading) => set((state) => ({
        tabLoadingStates: {
            ...state.tabLoadingStates,
            [tabType]: loading
        }
    })),
    
    // 탭별 선택 상태 (단일 선택)
    tabSelectedIds: {
        total: undefined,
        item: undefined,
        warehouse: undefined,
        logiccenter: undefined,
        lot: undefined
    },
    setTabSelectedId: (tabType, id) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: id
        }
    })),
    clearTabSelectedIds: (tabType) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: undefined
        }
    })),
    
    // === 소계 관련 상태 ===
    showSubtotals: false,
    setShowSubtotals: (show) => set({ showSubtotals: show }),
    
    // 탭별 소계 데이터
    tabSubtotalData: {
        item: [],
        warehouse: [],
        logiccenter: [],
        total: [],
        lot: [],
    },
    setTabSubtotalData: (tabType, data) => set((state) => ({
        tabSubtotalData: {
            ...state.tabSubtotalData,
            [tabType]: data
        }
    })),
    
    // 탭별 그리드 데이터 (소계 포함)
    tabGridData: {
        item: [],
        warehouse: [],
        logiccenter: [],
        total: [],
        lot: [],
    },
    setTabGridData: (tabType, data) => set((state) => ({
        tabGridData: {
            ...state.tabGridData,
            [tabType]: data
        }
    })),
    
    // 초기화
    initData: () => set({
        KSK1080_R01Filter: {
            logicCenter: '',
            warehouseCd: '',
            itemCd: '',
			itemNm: '',
            itemGroup: '',
            brandCd: ''
        },
        activeTab: 'total',
        tabData: {
            total: [],
            item: [],
            warehouse: [],
            logiccenter: [],
            lot: []
        },
        tabColumnStates: {
            total: [],
            item: [],
            warehouse: [],
            logiccenter: [],
            lot: []
        },
        tabLoadingStates: {
            total: false,
            item: false,
            warehouse: false,
            logiccenter: false,
            lot: false
        },
        tabSelectedIds: {
            total: undefined,
            item: undefined,
            warehouse: undefined,
            logiccenter: undefined,
            lot: undefined
        },
        showSubtotals: false,
        tabSubtotalData: {
            item: [],
            warehouse: [],
            logiccenter: [],
            total: [],
            lot: [],
        },
        tabGridData: {
            item: [],
            warehouse: [],
            logiccenter: [],
            total: [],
            lot: [],
        }
    }),
}));

export default useKSK1080_R01Store;
